import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Checkbox } from '@/components/ui/checkbox.tsx';
import { Button } from '@/components/ui/button.tsx';
import { LuChevronDown, LuInfo } from 'react-icons/lu';
import { cn } from '@/lib/utils.ts';
import { useSettingsStore } from '@/store/SettingsStore.ts';
import ScreenshotSection from '@/components/feedback/ScreenshotSection.tsx';
import { LoadingSpinner } from '@/components/ui/spinner.tsx';
import { toast } from 'sonner';
import { useAuthStore } from '@/store/AuthStore.ts';
import intermind from 'intermind';

function SendFeedbackPanelContent({ mode, onPostSubmit }: { mode: 'issue' | 'idea'; onPostSubmit: () => void }) {
  const { t } = useTranslation();
  const [category, setCategory] = useState('');
  const [message, setMessage] = useState('');
  const [agree, setAgree] = useState(false);
  const language = useSettingsStore.getState().language;
  const [screenshotBlob, setScreenshotBlob] = useState<Blob | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const isAuthorized = !!useAuthStore((state) => state.user);
  const [emailInput, setEmailInput] = useState('');

  const categories = [
    t('feedback.categories.join'),
    t('feedback.categories.audio'),
    t('feedback.categories.video'),
    t('feedback.categories.present'),
    t('feedback.categories.view'),
    t('feedback.categories.other'),
  ];

  const handleSubmit = async () => {
    try {
      setSubmitting(true);

      const basePayload = {
        message: message.trim(),
        screenshotBlob,
        agreeToContact: isAuthorized ? agree : !!emailInput.trim(),
        language,
      };

      const ok =
        mode === 'issue'
          ? await intermind.feedback.reportIssue({
              ...basePayload,
              category,
              email: !isAuthorized ? emailInput.trim() : undefined,
            })
          : await intermind.feedback.sendIdea({
              ...basePayload,
              email: !isAuthorized ? emailInput.trim() : undefined,
            });

      if (ok) {
        onPostSubmit();
        toast.success(t('feedback.success'));
      }
    } catch (error) {
      console.error('Error sending feedback:', error);
      toast.error(t('feedback.errorSending'));
    } finally {
      setSubmitting(false);
    }
  };

  const isValid = (category || mode === 'idea') && message.trim().length > 0;

  return (
    <form
      className="flex flex-col h-full"
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      <div className="flex flex-col flex-grow overflow-y-auto">
        <div className="flex flex-col flex-grow gap-6 pt-4">
          {mode === 'issue' && (
            <div>
              <label className="block text-sm font-medium mb-1">{t('feedback.question')}</label>
              <div className="relative">
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="appearance-none w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="" disabled hidden>
                    {t('feedback.chooseOption')}
                  </option>
                  {categories.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <LuChevronDown className="absolute right-3 top-2.5 h-4 w-4 text-gray-500 pointer-events-none" />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">
              {mode === 'issue' ? t('feedback.describeIssuePrompt') : t('feedback.describeIdeaPrompt')}
            </label>
            <textarea
              className="w-full border border-gray-300 rounded-md p-3 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              rows={5}
              placeholder={mode === 'issue' ? t('feedback.issuePlaceholder') : t('feedback.ideaPlaceholder')}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
            <div className="mt-2 text-xs text-gray-500 flex items-center gap-1">
              {t('feedback.noSensitiveInfo')}
              <LuInfo className="w-4 h-4 text-gray-400" title={t('feedback.noSensitiveTooltip')} />
            </div>
          </div>

          <ScreenshotSection mode={mode} screenshotBlob={screenshotBlob} setScreenshotBlob={setScreenshotBlob} />

          <div className="flex-grow" />

          <div className="py-4">
            {isAuthorized ? (
              <div className="flex items-start gap-2">
                <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} id="emailConsent" />
                <label htmlFor="emailConsent" className="text-sm">
                  {t('feedback.emailConsent')}
                </label>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <label htmlFor="anonEmail" className="text-sm">
                  {t('feedback.emailOptionalFull')}
                </label>
                <input
                  type="email"
                  id="anonEmail"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder={t('feedback.emailPlaceholder')}
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                />
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              {t('feedback.legalText.part1')}{' '}
              <a
                className="underline hover:text-primary"
                href={`https://mind.com${language && language !== 'en' ? `/${language}` : ''}/resources/company/Privacy-Policy`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('feedback.legalText.link')}
              </a>
              .
            </p>
          </div>
        </div>
      </div>

      <div className="pt-3 border-t border-gray-200 bg-white">
        <Button
          type="submit"
          className={cn(
            'w-full h-12 rounded-xl text-sm font-medium transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:pointer-events-none disabled:opacity-50 shadow bg-indigo-600 hover:bg-indigo-700 text-white active:bg-indigo-800'
          )}
          disabled={!isValid || submitting}
        >
          {submitting ? (
            <>
              <LoadingSpinner size={6} className="mr-2 text-white" />
              {t('feedback.sending')}
            </>
          ) : (
            t('feedback.send')
          )}
        </Button>
      </div>
    </form>
  );
}

export function ReportIssuePanelContent({ onPostSubmit }: { onPostSubmit: () => void }) {
  return <SendFeedbackPanelContent mode="issue" onPostSubmit={onPostSubmit} />;
}

export function SuggestIdeaPanelContent({ onPostSubmit }: { onPostSubmit: () => void }) {
  return <SendFeedbackPanelContent mode="idea" onPostSubmit={onPostSubmit} />;
}
